import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { AncillarySubtype, IGetProductReturn, IGetRatePlanReturn, IGetTotalToBePaidReturn } from "../../../../../api/services";
import DialogCloseButton from "../../../../../components/DialogCloseButton";
import { Nullable, ReactQueryKey } from "../../../../../types";
import { getCarGroupCodewithLabel, setDecimalSeparator } from "../../../../../utils";
import { IFormValues } from "../../../types";
import { idSmProductDefaultByDraftState } from "../../../recoil";
import { each } from "underscore";

interface IConfirmDialogProps {
    title: string,
    open: boolean,
    onClose: () => void,
    onConfirm: () => void,
    isOffer: boolean
}

interface IConfirmDialogValues {
    pickupLocation: string,
    dropoffLocation: string,
    pickupDate: string,
    dropoffDate: string,
    carGroup: string,
    mileageIncluded: string,
    selectedAncillaries: string[],
    totals: string
}

const ConfirmDialog = (props: IConfirmDialogProps) => {
    // console.log({ props })

    const { t, i18n } = useTranslation('booking');

    const formContext = useFormContext<IFormValues>();
    const formState = useFormState<IFormValues>();

    const idSmProductDefault = useRecoilValue(idSmProductDefaultByDraftState);

    const queryClient = useQueryClient();

    const [confirmValues, setConfirmValues] = useState<Nullable<IConfirmDialogValues>>(null);

    const getMissingFieldMessages = useCallback(() => {
        if (!confirmValues) {
            return [];
        }
        const messages: string[] = []
        const firstDriver = formContext.getValues('firstDriver');
        if (firstDriver.birthNation === '')
            messages.push(t('message.warning.missing_main_driver_nation_of_birth'));
        if (firstDriver.birthPlace === '')
            messages.push(t('message.warning.missing_main_driver_place_of_birth'));
        if (firstDriver.residenceCountry === '')
            messages.push(t('message.warning.missing_main_driver_contry_of_residence'));
        if (firstDriver.fiscalCode === '' && firstDriver.residenceCountry !== "FR")
            messages.push(t('message.warning.missing_main_driver_fiscal_code'));
        if (firstDriver.drivingLicenseNumber === '')
            messages.push(t('message.warning.missing_driving_license_number'));
        if (firstDriver.email === '')
            messages.push(t('message.warning.missing_main_driver_email'));
        return messages;
    }, [confirmValues, t]);

    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    function getTotalToBePaidString(total: any, product: any) {
        var productCode: number;
        productCode = product?.products?.find((e: any) => e.code === idSmProductDefault)?.code;

        return !productCode ? '' :
            String(setDecimalSeparator(((total?.totalToBePaid[productCode]?.[1].total ?? 0) / 100).toFixed(2), i18n.language)) +
            ' ' +
            total?.totalToBePaid[productCode]?.[1].currency +
            ' (' +
            product?.products?.find((e: any) => e.code === productCode)?.label +            
            ')' ;
    }

    useEffect(() => {
        if (props.open) {
            const carGroup = formContext.getValues('basicDetails.carGroup');
            const ratePlan = queryClient.getQueryData<IGetRatePlanReturn>([ReactQueryKey.RatePlan, { tenant: tenant as string, dealer: dealer as string }]);
            const total = queryClient.getQueryData<IGetTotalToBePaidReturn>([ReactQueryKey.TotalToBePaid, { tenant: tenant as string, dealer: dealer as string }]);
            const product = queryClient.getQueryData<IGetProductReturn>([ReactQueryKey.Product, { segment: formContext.getValues('basicDetails.rentalType'), tenant: tenant as string, dealer: dealer as string }]);
            const mileageIncluded = formContext.getValues('basicDetails.mileageIncluded');
            setConfirmValues({
                pickupLocation: formContext.getValues('basicDetails.pickupLocation')?.label ?? '',
                dropoffLocation: formContext.getValues('basicDetails.dropoffLocation')?.label ?? '',
                pickupDate: formContext.getValues('basicDetails.pickupDate')?.format('DD-MM-YYYY HH:mm') ?? '',
                dropoffDate: formContext.getValues('basicDetails.dropoffDate')?.format('DD-MM-YYYY HH:mm') ?? '',
                carGroup: carGroup ? getCarGroupCodewithLabel(carGroup) : '',
                mileageIncluded: (mileageIncluded === '0KM' || mileageIncluded === '0') ? 'UNLIMITED' : mileageIncluded ?? '',
                selectedAncillaries: Object
                    .entries(formContext.getValues('extraServices.ancillary'))
                    .reduce((arr: string[], [key, value]: [string, number | boolean]) => {
                        const ancillaryName = ratePlan?.ancillaries.find(a => {
                            if (key === AncillarySubtype.AdditionalDrivers) {
                                return a.subtype === key;
                            }
                            return a.ancillaryCode === key;
                        })?.name;
                        const resolvedAncillaryLabel = ancillaryName?.[i18n.language] ?? ancillaryName?.defaultLabel ?? '';
                        switch (typeof value) {
                            case 'number':
                                value > 0 && arr.push(resolvedAncillaryLabel + ` (x${value})`);
                                break;
                            case 'boolean':
                                value === true && arr.push(resolvedAncillaryLabel);
                                break;
                        }
                        return arr;
                    }, []),
                totals: getTotalToBePaidString(total, product)
            })
        }
    }, [props.open])

    return (
        <Dialog open={props.open}>
            <DialogCloseButton onClick={props.onClose} />
            <DialogTitle>{props.title}</DialogTitle>
            <DialogContent>
                <Grid container p={2} rowSpacing={2}>
                    <Grid item xs={6}>
                        <Typography variant="bodySmall">
                            {t('label.pickupLocation')}:
                        </Typography>
                        <Typography>{confirmValues?.pickupLocation}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="bodySmall">
                            {t('label.dropoffLocation')}:
                        </Typography>
                        <Typography>{confirmValues?.dropoffLocation}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="bodySmall">
                            {t('label.pickupDate')}:
                        </Typography>
                        <Typography>{confirmValues?.pickupDate}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="bodySmall">
                            {t('label.dropoffDate')}:
                        </Typography>
                        <Typography>{confirmValues?.dropoffDate}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="bodySmall">
                            {t('label.carGroup')}:
                        </Typography>
                        <Typography>{confirmValues?.carGroup}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="bodySmall">
                            {t('label.mileageIncluded')}:
                        </Typography>
                        <Typography>{confirmValues?.mileageIncluded}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="bodySmall">
                            {t('label.selectedAncillaries')}:
                        </Typography>
                        {confirmValues?.selectedAncillaries.map((sa, i) => <Typography key={i}>{sa}</Typography>)}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="bodySmall">
                            {t('label.totalToBePaid')}:
                        </Typography>
                        <Typography>{confirmValues?.totals}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {getMissingFieldMessages().map((message, i) => (
                            <Typography key={i} color="red">{message}</Typography>
                        ))}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    variant='outlined'
                    onClick={props.onClose}
                >
                    {t('label.back')}
                </Button>
                <Button
                    onClick={props.onConfirm}
                    disabled={formState.isSubmitting}
                >
                    {t('label.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDialog;
